import React, { useState, useEffect, useRef } from "react";
import "../index.css"; // Use shared styles
import maintenanceTasksData from "../data/maintenanceTasks.json";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import { Textarea } from "../components/ui/textarea";
// Tabs removed - now using unified interface
import { Badge } from "../components/ui/badge";
import { Separator } from "../components/ui/separator";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { AlertCircle, Download, Upload, Printer, Plus, X, Save, FileText, Car, CreditCard, MapPin, ChevronDown, ChevronUp, ChevronRight,
         AlertTriangle, Users, Scale, Home, Zap, DollarSign, Briefcase, Heart, Church, Globe, 
         Stethoscope, Cloud, Folder, Package, Smile, CheckCircle, Wrench } from "lucide-react";
import EmojiPicker from 'emoji-picker-react';
import { toast } from "sonner";
import TaskCard from "../components/TaskCard";
import ToolLayout from "../components/ToolLayout";

// Backend URL no longer needed - using embedded data

// Category icon mapping
const CATEGORY_ICONS = {
  "Critical / Life-Threatening Emergencies": AlertTriangle,
  "Family & Friends": Users,
  "Legal / Advocacy": Scale,
  "Transportation & Auto": Car,
  "Home / Utilities": Home,
  "Financial & Identity": DollarSign,
  "Work & Security": Briefcase,
  "Support & Crisis Resources": Heart,
  "Religious / Faith-Based Support": Church,
  "Travel / Abroad": Globe,
  "Animals & Pets": Stethoscope,
  "Disaster / Survival": Cloud,
  "Identification & Records": Folder,
  "Logistics & Deliveries": Package,
  // Custom category icons
  "Personal Contacts": Users,
  "Work Emergency": Briefcase,
  "Neighborhood": Home,
  "Extended Family": Users
};

// Predefined category list for icon mapping
const PREDEFINED_CATEGORIES = [
  'Critical / Life-Threatening Emergencies',
  'Family & Friends', 
  'Legal / Advocacy', 
  'Transportation & Auto', 
  'Home / Utilities', 
  'Financial & Identity', 
  'Work & Security', 
  'Support & Crisis Resources', 
  'Religious / Faith-Based Support', 
  'Travel / Abroad', 
  'Animals & Pets', 
  'Disaster / Survival', 
  'Identification & Records', 
  'Logistics & Deliveries'
];

// Predefined category emoji mapping
const CATEGORY_EMOJIS = {
  "Critical / Life-Threatening Emergencies": "🚨",
  "Family & Friends": "👥",
  "Legal / Advocacy": "⚖️",
  "Transportation & Auto": "🚗",
  "Home / Utilities": "🏠",
  "Financial & Identity": "💳",
  "Work & Security": "💼",
  "Support & Crisis Resources": "❤️",
  "Religious / Faith-Based Support": "⛪",
  "Travel / Abroad": "🌍",
  "Animals & Pets": "🐾",
  "Disaster / Survival": "⛈️",
  "Identification & Records": "📁",
  "Logistics & Deliveries": "📦"
};

function MaintenanceApp() {
  const [masterContacts, setMasterContacts] = useState({});
  const [selectedContacts, setSelectedContacts] = useState(new Set());
  // Animation tracking
  const [scoreAnimating, setScoreAnimating] = useState(false);
  const prevReadinessScore = useRef(null);
  const [contactData, setContactData] = useState({});
  const [templateAssignments, setTemplateAssignments] = useState({
    // Structure: { contactId: { master: true, travel: false, wallet: true } }
  });
  const [customContacts, setCustomContacts] = useState([]);
  const [injectedContacts, setInjectedContacts] = useState({}); // Maps contactKey to array of injected contacts
  const [contactOrder, setContactOrder] = useState([]); // Track the order of selected contacts
  const [collapsedContacts, setCollapsedContacts] = useState(new Set()); // Track which contacts are collapsed
  const [collapsedCustomContacts, setCollapsedCustomContacts] = useState(new Set());
  const [collapsedCategories, setCollapsedCategories] = useState(new Set()); // Track which categories are collapsed
  const [categoryOrder, setCategoryOrder] = useState([]); // Track the order of categories
  const [userName, setUserName] = useState("");
  const [loading, setLoading] = useState(false);
  const [showCustomContactDialog, setShowCustomContactDialog] = useState(false);
  const [newCustomContact, setNewCustomContact] = useState({
    name: "",
    address: "",
    tools: "",
    category: "",
    subcategory: "",
    notes: ""
  });
  const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);
  const [showCustomCategoryDialog, setShowCustomCategoryDialog] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [newCustomCategory, setNewCustomCategory] = useState({
    name: "",
    icon: ""
  });
  const [customCategoryIcons, setCustomCategoryIcons] = useState({}); // Store custom category icons
  const [showDeleteConfirmDialog, setShowDeleteConfirmDialog] = useState(false);
  const [deleteConfirmData, setDeleteConfirmData] = useState({ type: '', id: '', name: '' });
  const [highlightedItems, setHighlightedItems] = useState(new Set());
  const categoryRefs = useRef({});
  const contactRefs = useRef({});
  const initialCollapsedMonthsSet = useRef(false);
  const [printMode, setPrintMode] = useState("");

  // Load master contacts on component mount
  useEffect(() => {
    fetchMasterContacts();
  }, []);

  // Default all months (categories) to collapsed on first load
  useEffect(() => {
    const categories = Object.keys(masterContacts);
    if (categories.length === 0 || initialCollapsedMonthsSet.current) return;
    initialCollapsedMonthsSet.current = true;
    setCollapsedCategories(new Set(categories));
  }, [masterContacts]);

  // Close category dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showCategoryDropdown && !event.target.closest('.category-dropdown-container')) {
        setShowCategoryDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showCategoryDropdown]);

  const getCategoryIcon = (categoryName) => {
    const IconComponent = CATEGORY_ICONS[categoryName];
    return IconComponent ? IconComponent : AlertCircle; // Fallback icon
  };

  const fetchMasterContacts = () => {
    try {
      // Use embedded data directly - no API call needed
      const masterMaintenanceData = maintenanceTasksData;
      setMasterContacts(masterMaintenanceData);
      
      // Smart preloading - select ALL tasks for ALL months and collapse them for clean overview
      const preselectedTasks = new Set();
      const collapsedTasks = new Set();
      const monthNames = ['January', 'February', 'March', 'April', 'May', 'June',
        'July', 'August', 'September', 'October', 'November', 'December'];
      
      // Pre-select ALL tasks from ALL months and collapse them for a clean comprehensive overview
      let totalTasksLoaded = 0;
      monthNames.forEach(monthName => {
        if (masterMaintenanceData[monthName]) {
          masterMaintenanceData[monthName].forEach((task, index) => {
            const contactKey = `${monthName}-${index}`;
            preselectedTasks.add(contactKey);
            collapsedTasks.add(contactKey); // Collapse by default for clean overview
            totalTasksLoaded++;
          });
        }
      });
      
      if (totalTasksLoaded > 0) {
        setSelectedContacts(preselectedTasks);
        setCollapsedContacts(collapsedTasks); // Set all as collapsed
        toast.success(`Pre-loaded all ${totalTasksLoaded} essential maintenance tasks for the entire year (click categories to expand and customize)`);
      } else {
        // Fallback message if no tasks found
        toast.info(`Maintenance schedule loaded. Please select tasks manually.`);
      }
      
    } catch (error) {
      console.error("Error loading maintenance tasks:", error);
      toast.error("Failed to load maintenance tasks");
      
      // Set empty master contacts so the UI still works
      setMasterContacts({});
    }
  };

  const handleContactDataChange = (contactKey, field, value) => {
    // Check if this is an injected contact
    if (contactKey.includes('-injected-')) {
      // Update injected contact data
      const originalKey = contactKey.split('-injected-')[0];
      const injectedKey = contactKey;
      
      setInjectedContacts(prev => ({
        ...prev,
        [originalKey]: prev[originalKey]?.map(contact => 
          contact.injectedKey === injectedKey
            ? { ...contact, [field]: value }
            : contact
        ) || []
      }));
    } else {
      // Handle regular contact data
      setContactData(prev => ({
        ...prev,
        [contactKey]: {
          ...prev[contactKey],
          [field]: value
        }
      }));
    }
  };

  const handleContactToggle = (contactKey, checked) => {
    setSelectedContacts(prev => {
      const newSelected = new Set(prev);
      if (checked) {
        newSelected.add(contactKey);
        // Add to the end of the contact order if not already present
        setContactOrder(prevOrder => {
          if (!prevOrder.includes(contactKey)) {
            return [...prevOrder, contactKey];
          }
          return prevOrder;
        });
      } else {
        newSelected.delete(contactKey);
        // Remove from contact order
        setContactOrder(prevOrder => prevOrder.filter(id => id !== contactKey));
      }
      return newSelected;
    });
  };

  const handleTemplateAssignment = (contactId, template, assigned) => {
    setTemplateAssignments(prev => ({
      ...prev,
      [contactId]: {
        ...prev[contactId],
        [template]: assigned
      }
    }));
  };

  const isContactAssignedToTemplate = (contactId, template) => {
    return templateAssignments[contactId]?.[template] || false;
  };

  const isContactSelected = (contactKey) => {
    return selectedContacts.has(contactKey);
  };

  const addCustomContact = () => {
    if (!newCustomContact.name) {
      toast.error("Please fill in contact name");
      return;
    }

    const customContact = {
      ...newCustomContact,
      id: `custom-${Date.now()}`,
      is_custom: true
    };

    setCustomContacts(prev => [...prev, customContact]);
    // Auto-select the new custom contact
    setSelectedContacts(prev => new Set([...prev, customContact.id]));
    
    const targetCategory = newCustomContact.category || 'Uncategorized';
    
    setNewCustomContact({
      name: "",
      address: "",
      tools: "",
      category: "",
      subcategory: "",
      notes: ""
    });
    setShowCustomContactDialog(false);
    toast.success("Custom contact added successfully");
    
    // Focus and highlight the new contact
    focusAndHighlightContact(customContact.id, targetCategory);
  };

  const addCustomCategory = () => {
    if (!newCustomCategory.name) {
      toast.error("Please enter a category name");
      return;
    }

    const categoryName = newCustomCategory.name.trim();
    
    // Check if category already exists
    if (masterContacts[categoryName]) {
      toast.error("A category with this name already exists");
      return;
    }

    // Add the new category to master contacts with empty array
    setMasterContacts(prev => ({
      ...prev,
      [categoryName]: []
    }));

    // New custom category (month) starts collapsed like the rest
    setCollapsedCategories(prev => new Set([...prev, categoryName]));

    // Store the custom category icon if provided
    if (newCustomCategory.icon.trim()) {
      setCustomCategoryIcons(prev => ({
        ...prev,
        [categoryName]: newCustomCategory.icon.trim()
      }));
    }

    setNewCustomCategory({
      name: "",
      icon: ""
    });
    setShowCustomCategoryDialog(false);
    setShowEmojiPicker(false);
    toast.success(`Category "${categoryName}" added successfully`);
    
    // Focus and highlight the new category
    focusAndHighlightCategory(categoryName);
  };

  const onEmojiClick = (emojiData) => {
    setNewCustomCategory(prev => ({ 
      ...prev, 
      icon: emojiData.emoji 
    }));
    setShowEmojiPicker(false);
  };

  // Get category emoji (predefined or custom)
  const getCategoryEmoji = (category) => {
    // Check if it's a custom category with a custom icon
    if (customCategoryIcons[category]) {
      return customCategoryIcons[category];
    }
    // Check if it's a predefined category
    if (CATEGORY_EMOJIS[category]) {
      return CATEGORY_EMOJIS[category];
    }
    // Default fallback for categories without icons
    return "📋";
  };

  // Confirmation and deletion functions
  const confirmDeleteContact = (contactId, contactName, isCustom = false) => {
    setDeleteConfirmData({
      type: isCustom ? 'custom-contact' : 'master-contact',
      id: contactId,
      name: contactName
    });
    setShowDeleteConfirmDialog(true);
  };

  const confirmDeleteCategory = (categoryName) => {
    setDeleteConfirmData({
      type: 'category',
      id: categoryName,
      name: categoryName
    });
    setShowDeleteConfirmDialog(true);
  };

  const executeDelete = () => {
    const { type, id, name } = deleteConfirmData;
    
    if (type === 'custom-contact') {
      removeCustomContact(id);
    } else if (type === 'master-contact') {
      removeMasterContact(id);
    } else if (type === 'category') {
      removeCategory(id);
    }
    
    setShowDeleteConfirmDialog(false);
    setDeleteConfirmData({ type: '', id: '', name: '' });
  };

  const removeMasterContact = (contactKey) => {
    // Parse the contact key to get category and index
    const [category, indexStr] = contactKey.split('-');
    const index = parseInt(indexStr);
    
    // Remove from master contacts
    const updatedMasterContacts = { ...masterContacts };
    if (updatedMasterContacts[category]) {
      updatedMasterContacts[category] = updatedMasterContacts[category].filter((_, i) => i !== index);
      setMasterContacts(updatedMasterContacts);
    }
    
    // Clean up related state
    if (selectedContacts.has(contactKey)) {
      const newSelected = new Set(selectedContacts);
      newSelected.delete(contactKey);
      setSelectedContacts(newSelected);
    }
    
    // Clean up contact data
    const updatedContactData = { ...contactData };
    delete updatedContactData[contactKey];
    setContactData(updatedContactData);
    
    // Clean up collapsed state
    const newCollapsed = new Set(collapsedContacts);
    newCollapsed.delete(contactKey);
    setCollapsedContacts(newCollapsed);
    
    toast.success("Contact removed successfully");
  };

  const removeCategory = (categoryName) => {
    // Remove from master contacts
    const updatedMasterContacts = { ...masterContacts };
    delete updatedMasterContacts[categoryName];
    setMasterContacts(updatedMasterContacts);
    
    // Remove custom contacts in this category
    const updatedCustomContacts = customContacts.filter(
      contact => (contact.category || 'Uncategorized') !== categoryName
    );
    setCustomContacts(updatedCustomContacts);
    
    // Remove custom category icon
    const updatedIcons = { ...customCategoryIcons };
    delete updatedIcons[categoryName];
    setCustomCategoryIcons(updatedIcons);
    
    // Clean up category collapse state
    const newCollapsedCategories = new Set(collapsedCategories);
    newCollapsedCategories.delete(categoryName);
    setCollapsedCategories(newCollapsedCategories);
    
    toast.success(`Category "${categoryName}" removed successfully`);
  };

  // Focus and highlight new items
  const focusAndHighlightCategory = (categoryName) => {
    // Add to highlighted items
    setHighlightedItems(prev => new Set([...prev, `category-${categoryName}`]));
    
    // Remove highlight after animation
    setTimeout(() => {
      setHighlightedItems(prev => {
        const newSet = new Set(prev);
        newSet.delete(`category-${categoryName}`);
        return newSet;
      });
    }, 3000);
    
    // Scroll to category after a brief delay to ensure rendering
    setTimeout(() => {
      try {
        if (categoryRefs.current && categoryRefs.current[categoryName]) {
          categoryRefs.current[categoryName].scrollIntoView({
            behavior: 'smooth',
            block: 'center'
          });
        }
      } catch (error) {
        console.warn('Error scrolling to category:', error);
      }
    }, 100);
  };

  const focusAndHighlightContact = (contactId, category) => {
    // Add to highlighted items
    setHighlightedItems(prev => new Set([...prev, `contact-${contactId}`]));
    
    // Remove highlight after animation
    setTimeout(() => {
      setHighlightedItems(prev => {
        const newSet = new Set(prev);
        newSet.delete(`contact-${contactId}`);
        return newSet;
      });
    }, 3000);
    
    // Scroll to category first, then contact
    setTimeout(() => {
      // Ensure category is expanded
      if (collapsedCategories.has(category)) {
        toggleCategoryCollapse(category);
      }
      
      // Scroll to category
      try {
        if (categoryRefs.current && categoryRefs.current[category]) {
          categoryRefs.current[category].scrollIntoView({
            behavior: 'smooth',
            block: 'start'
          });
        }
      } catch (error) {
        console.warn('Error scrolling to category:', error);
      }
      
      // Then scroll to specific contact after category scroll
      setTimeout(() => {
        try {
          if (contactRefs.current && contactRefs.current[contactId]) {
            contactRefs.current[contactId].scrollIntoView({
              behavior: 'smooth',
              block: 'center'
            });
          }
        } catch (error) {
          console.warn('Error scrolling to contact:', error);
        }
      }, 500);
    }, 100);
  };

  // Get all contacts for a category (master + custom)
  const getAllContactsForCategory = (category) => {
    const masterContactsForCategory = masterContacts[category] || [];
    const customContactsForCategory = customContacts.filter(contact => 
      (contact.category || 'Uncategorized') === category
    );

    // Convert custom contacts to the same format as master contacts
    const formattedCustomContacts = customContactsForCategory.map(contact => ({
      ...contact,
      is_custom: true
    }));

    return [...masterContactsForCategory, ...formattedCustomContacts];
  };

  // Get ordered categories including custom categories
  const getAllOrderedCategories = () => {
    // Get all categories from master contacts
    const masterCategories = Object.keys(masterContacts);
    
    // Get all unique categories from custom contacts
    const customContactCategories = [...new Set(
      customContacts.map(contact => contact.category || 'Uncategorized')
    )];
    
    // Combine and deduplicate
    const allCategories = [...new Set([...masterCategories, ...customContactCategories])];
    
    // Keep ALL categories (including empty ones) - users can delete them manually if needed
    
    // Apply user ordering if it exists
    if (categoryOrder.length > 0) {
      const orderedCategories = categoryOrder.filter(cat => allCategories.includes(cat));
      const remainingCategories = allCategories.filter(cat => !categoryOrder.includes(cat));
      return [...orderedCategories, ...remainingCategories];
    }
    
    return allCategories;
  };

  const removeCustomContact = (contactId) => {
    setCustomContacts(prev => prev.filter(c => c.id !== contactId));
    setSelectedContacts(prev => {
      const newSelected = new Set(prev);
      newSelected.delete(contactId);
      return newSelected;
    });
  };

  const updateCustomContact = (contactId, field, value) => {
    setCustomContacts(prev =>
      prev.map(contact =>
        contact.id === contactId
          ? { ...contact, [field]: value }
          : contact
      )
    );
  };

  const addAnotherContact = (category, contactTemplate, originalIndex) => {
    const originalContactKey = `${category}-${originalIndex}`;
    
    // Count existing injected contacts for this original contact to determine the next number
    const existingInjected = injectedContacts[originalContactKey] || [];
    const nextNumber = existingInjected.length + 2; // +2 because original is #1, first duplicate is #2
    
    const newContactKey = `${originalContactKey}-injected-${Date.now()}`;
    const newContact = {
      ...contactTemplate,
      name: `${contactTemplate.name} #${nextNumber}`,
      originalKey: originalContactKey,
      injectedKey: newContactKey,
      is_injected: true,
      address: "", // Clear address for user to fill in
      tools: "", // Clear tools for user to fill in
      notes: "",
      category: category
    };

    // Add to injected contacts
    setInjectedContacts(prev => ({
      ...prev,
      [originalContactKey]: [...(prev[originalContactKey] || []), newContact]
    }));
    
    // Auto-select the new contact
    setSelectedContacts(prev => new Set([...prev, newContactKey]));
    toast.success(`Added another "${contactTemplate.name}" contact below`);
  };

  const removeInjectedContact = (originalContactKey, injectedKey) => {
    // Remove from injected contacts
    setInjectedContacts(prev => ({
      ...prev,
      [originalContactKey]: prev[originalContactKey]?.filter(contact => 
        contact.injectedKey !== injectedKey
      ) || []
    }));
    
    // Remove from selected contacts
    setSelectedContacts(prev => {
      const newSelected = new Set(prev);
      newSelected.delete(injectedKey);
      return newSelected;
    });
    
    // Remove from template assignments
    setTemplateAssignments(prev => {
      const updated = { ...prev };
      delete updated[injectedKey];
      return updated;
    });

    // Remove from contact order
    setContactOrder(prev => prev.filter(id => id !== injectedKey));
    
    toast.success("Contact removed successfully");
  };

  // Collapse/expand handlers
  const toggleContactCollapse = (contactKey) => {
    setCollapsedContacts(prev => {
      const newCollapsed = new Set(prev);
      if (newCollapsed.has(contactKey)) {
        newCollapsed.delete(contactKey);
      } else {
        newCollapsed.add(contactKey);
      }
      return newCollapsed;
    });
  };

  const toggleCustomContactCollapse = (contactId) => {
    setCollapsedCustomContacts(prev => {
      const newCollapsed = new Set(prev);
      if (newCollapsed.has(contactId)) {
        newCollapsed.delete(contactId);
      } else {
        newCollapsed.add(contactId);
      }
      return newCollapsed;
    });
  };

  const isContactCollapsed = (contactKey) => {
    return collapsedContacts.has(contactKey);
  };

  const isCustomContactCollapsed = (contactId) => {
    return collapsedCustomContacts.has(contactId);
  };

  // Category collapse/expand handlers
  const toggleCategoryCollapse = (category) => {
    setCollapsedCategories(prev => {
      const newCollapsed = new Set(prev);
      if (newCollapsed.has(category)) {
        newCollapsed.delete(category);
      } else {
        newCollapsed.add(category);
      }
      return newCollapsed;
    });
  };

  const isCategoryCollapsed = (category) => {
    return collapsedCategories.has(category);
  };

  // Get ordered categories (respects drag-and-drop order)
  const getOrderedCategories = () => {
    const allCategories = Object.keys(masterContacts);
    
    if (categoryOrder.length === 0) {
      return allCategories;
    }
    
    // Sort based on the stored order
    const orderedCategories = [];
    const categorySet = new Set(allCategories);
    
    // Add categories in the specified order
    categoryOrder.forEach(category => {
      if (categorySet.has(category)) {
        orderedCategories.push(category);
        categorySet.delete(category);
      }
    });
    
    // Add any remaining categories that weren't in the order (newly added)
    categorySet.forEach(category => {
      orderedCategories.push(category);
    });
    
    return orderedCategories;
  };

  // Category drag and drop handlers
  const handleCategoryDragStart = (e, category, index) => {
    e.dataTransfer.setData('text/plain', category);
    e.dataTransfer.setData('application/json', JSON.stringify({ category, index, type: 'category' }));
    e.dataTransfer.effectAllowed = 'move';
    
    // Add visual feedback
    e.target.style.opacity = '0.5';
  };

  const handleCategoryDragEnd = (e) => {
    e.target.style.opacity = '1';
    // Remove all drag-over classes from all elements
    document.querySelectorAll('.drag-over, .drag-over-category').forEach(el => {
      el.classList.remove('drag-over', 'drag-over-category');
    });
  };

  const handleCategoryDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    
    // Check if it's a contact being dragged over a category
    try {
      const dragData = JSON.parse(e.dataTransfer.getData('application/json'));
      if (dragData.type === 'contact') {
        // Add visual feedback for category drop zones when contact is being dragged
        const categoryContainer = e.target.closest('[draggable="true"]');
        if (categoryContainer && !categoryContainer.classList.contains('drag-over-category')) {
          // Remove drag-over from other categories
          document.querySelectorAll('.drag-over-category').forEach(el => {
            el.classList.remove('drag-over-category');
          });
          
          // Add drag-over to current category
          categoryContainer.classList.add('drag-over-category');
        }
      }
    } catch (e) {
      // If we can't parse the drag data, that's okay - continue with normal category drag
    }
  };

  const handleCategoryDrop = (e, dropCategory, dropIndex) => {
    e.preventDefault();
    
    const dragData = JSON.parse(e.dataTransfer.getData('application/json'));
    const { category: dragCategory, index: dragIndex, type, contactKey } = dragData;
    
    if (type === 'category') {
      // Handle category reordering
      if (dragCategory === dropCategory || dragIndex === dropIndex) return;
      
      const categories = getOrderedCategories();
      const newOrder = [...categories];
      
      // Remove the dragged item and insert it at the new position
      const draggedItem = newOrder.splice(dragIndex, 1)[0];
      newOrder.splice(dropIndex, 0, draggedItem);
      
      setCategoryOrder(newOrder);
      toast.success("Category order updated");
    } else if (type === 'contact') {
      // Handle contact being dropped on a category (move to end of that category)
      if (dragCategory === dropCategory) return; // Same category, no need to move
      
      const originalContact = masterContacts[dragCategory][dragIndex];
      const newContactData = { ...originalContact };
      
      // Update master contacts - add to new category and remove from old
      const updatedMasterContacts = { ...masterContacts };
      
      // Remove from original category
      updatedMasterContacts[dragCategory] = updatedMasterContacts[dragCategory].filter((_, i) => i !== dragIndex);
      
      // Add to end of new category
      updatedMasterContacts[dropCategory] = [...updatedMasterContacts[dropCategory], newContactData];
      
      // Update the master contacts state
      setMasterContacts(updatedMasterContacts);
      
      // Update contact data keys
      const oldContactKey = `${dragCategory}-${dragIndex}`;
      const newContactIndex = updatedMasterContacts[dropCategory].length - 1;
      const newContactKey = `${dropCategory}-${newContactIndex}`;
      
      // Transfer any existing contact data to the new key
      if (contactData[oldContactKey]) {
        const updatedContactData = { ...contactData };
        updatedContactData[newContactKey] = updatedContactData[oldContactKey];
        delete updatedContactData[oldContactKey];
        setContactData(updatedContactData);
      }
      
      // Update selected contacts if the contact was selected
      if (selectedContacts.has(oldContactKey)) {
        const updatedSelectedContacts = new Set(selectedContacts);
        updatedSelectedContacts.delete(oldContactKey);
        updatedSelectedContacts.add(newContactKey);
        setSelectedContacts(updatedSelectedContacts);
      }
      
      toast.success(`Contact moved from "${dragCategory}" to "${dropCategory}"`);
    }
  };

  // Contact drag and drop handlers
  const handleContactDragStart = (e, contactKey, category, index) => {
    e.dataTransfer.setData('text/plain', contactKey);
    e.dataTransfer.setData('application/json', JSON.stringify({ 
      contactKey, 
      category, 
      index, 
      type: 'contact' 
    }));
    e.dataTransfer.effectAllowed = 'move';
    
    // Add visual feedback
    e.target.classList.add('dragging');
    e.stopPropagation(); // Prevent category drag
  };

  const handleContactDragEnd = (e) => {
    e.target.classList.remove('dragging');
    // Remove all drag-over classes from all elements
    document.querySelectorAll('.drag-over, .drag-over-category').forEach(el => {
      el.classList.remove('drag-over', 'drag-over-category');
    });
  };

  const handleContactDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    e.stopPropagation(); // Prevent category drag over
    
    // Add visual feedback for contact drop zones
    if (!e.target.classList.contains('drag-over')) {
      // Remove drag-over from other elements
      document.querySelectorAll('.drag-over').forEach(el => {
        el.classList.remove('drag-over');
      });
      
      // Add drag-over to current target
      const contactCard = e.target.closest('[draggable="true"]');
      if (contactCard) {
        contactCard.classList.add('drag-over');
      }
    }
  };

  const handleContactDrop = (e, dropContactKey, dropCategory, dropIndex) => {
    e.preventDefault();
    e.stopPropagation(); // Prevent category drop
    
    try {
      const dragData = JSON.parse(e.dataTransfer.getData('application/json'));
      const { contactKey: dragContactKey, contactId, category: dragCategory, index: dragIndex, type } = dragData;
      
      // Handle both master contacts and custom contacts
      if ((type !== 'contact' && type !== 'custom-contact') || 
          (dragContactKey === dropContactKey) || 
          (contactId === dropContactKey)) return;
      
      // Same contact, same position - no change needed
      if (dragCategory === dropCategory && dragIndex === dropIndex) return;
      
      // Handle custom contact drops
      if (type === 'custom-contact') {
        // Moving custom contact to different category
        const updatedContacts = customContacts.map(contact => 
          contact.id === contactId 
            ? { ...contact, category: dropCategory }
            : contact
        );
        
        setCustomContacts(updatedContacts);
        toast.success(`Contact moved from "${dragCategory}" to "${dropCategory}"`);
        return;
      }
      
      // Handle master contact drops (original logic)
      if (type === 'contact') {
        // Same contact, same position - no change needed
        if (dragCategory === dropCategory && dragIndex === dropIndex) return;
        
        // Handle inter-category movement (moving contact to different category)
        if (dragCategory !== dropCategory) {
          // Create new contact data for the new category
          const originalContact = masterContacts[dragCategory][dragIndex];
          const newContactData = { ...originalContact };
          
          // Update master contacts - add to new category and remove from old
          const updatedMasterContacts = { ...masterContacts };
          
          // Remove from original category
          updatedMasterContacts[dragCategory] = updatedMasterContacts[dragCategory].filter((_, i) => i !== dragIndex);
          
          // Add to new category at the drop position
          const newCategoryContacts = [...updatedMasterContacts[dropCategory]];
          newCategoryContacts.splice(dropIndex, 0, newContactData);
          updatedMasterContacts[dropCategory] = newCategoryContacts;
          
          // Update the master contacts state
          setMasterContacts(updatedMasterContacts);
          
          // Update contact data keys
          const oldContactKey = `${dragCategory}-${dragIndex}`;
          const newContactKey = `${dropCategory}-${dropIndex}`;
          
          // Transfer any existing contact data to the new key
          if (contactData[oldContactKey]) {
            const updatedContactData = { ...contactData };
            updatedContactData[newContactKey] = updatedContactData[oldContactKey];
            delete updatedContactData[oldContactKey];
            setContactData(updatedContactData);
          }
          
          // Update selected contacts if the contact was selected
          if (selectedContacts.has(oldContactKey)) {
            const updatedSelectedContacts = new Set(selectedContacts);
            updatedSelectedContacts.delete(oldContactKey);
            updatedSelectedContacts.add(newContactKey);
            setSelectedContacts(updatedSelectedContacts);
          }
          
          toast.success(`Contact moved from "${dragCategory}" to "${dropCategory}"`);
        } else {
          // Handle intra-category reordering (moving contact within same category)
          const updatedMasterContacts = { ...masterContacts };
          const categoryContacts = [...updatedMasterContacts[dragCategory]];
          
          // Remove the dragged contact and insert it at the new position
          const draggedContact = categoryContacts.splice(dragIndex, 1)[0];
          categoryContacts.splice(dropIndex, 0, draggedContact);
          
          updatedMasterContacts[dragCategory] = categoryContacts;
          setMasterContacts(updatedMasterContacts);
          
          toast.success("Contact order updated");
        }
      }
    } catch (error) {
      console.error('Error handling contact drop:', error);
      toast.error('Error moving contact');
    }
  };

  // Updated drag and drop handlers for all contacts (not just selected)
  const handleDragStart = (e, contactKey, index) => {
    e.dataTransfer.setData('text/plain', contactKey);
    e.dataTransfer.setData('application/json', JSON.stringify({ contactKey, index }));
    e.dataTransfer.effectAllowed = 'move';
    
    // Add visual feedback
    e.target.style.opacity = '0.5';
  };

  const handleDragEnd = (e) => {
    e.target.style.opacity = '1';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e, dropContactKey, dropIndex) => {
    e.preventDefault();
    
    const dragData = JSON.parse(e.dataTransfer.getData('application/json'));
    const { contactKey: dragContactKey, index: dragIndex } = dragData;
    
    if (dragContactKey === dropContactKey || dragIndex === dropIndex) return;
    
    // For now, we'll update the contact order state to remember user's preferred order
    // This will be used when rendering the contact list
    const allContactKeys = getAllContactKeys();
    const newOrder = [...allContactKeys];
    
    // Remove the dragged item and insert it at the new position
    const draggedItem = newOrder.splice(dragIndex, 1)[0];
    newOrder.splice(dropIndex, 0, draggedItem);
    
    setContactOrder(newOrder);
    toast.success("Contact order updated");
  };
  // Custom contact drag and drop handlers
  const handleCustomContactDragStart = (e, contactId, category) => {
    e.dataTransfer.setData('text/plain', contactId);
    e.dataTransfer.setData('application/json', JSON.stringify({ 
      contactId, 
      category, 
      type: 'custom-contact' 
    }));
    e.dataTransfer.effectAllowed = 'move';
    
    // Add visual feedback
    e.target.classList.add('dragging');
    e.stopPropagation(); // Prevent category drag
  };

  const handleCustomContactDragEnd = (e) => {
    e.target.classList.remove('dragging');
    // Remove all drag-over classes from all elements
    document.querySelectorAll('.drag-over, .drag-over-category').forEach(el => {
      el.classList.remove('drag-over', 'drag-over-category');
    });
  };

  const handleCustomContactDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    e.stopPropagation(); // Prevent category drag over
  };

  const handleCustomContactDrop = (e, dropContactId, dropCategory) => {
    // Use the unified contact drop handler
    handleContactDrop(e, dropContactId, dropCategory, 0);
  };

  // Get all contact keys in the current display order
  const getAllContactKeys = () => {
    const keys = [];
    Object.entries(masterContacts).forEach(([category, contacts]) => {
      contacts.forEach((contact, index) => {
        const contactKey = `${category}-${index}`;
        keys.push(contactKey);
        
        // Add injected contacts for this original contact
        const injectedForThisContact = injectedContacts[contactKey] || [];
        injectedForThisContact.forEach(injectedContact => {
          keys.push(injectedContact.injectedKey);
        });
      });
    });
    return keys;
  };

  // Get ordered contact keys (respects drag-and-drop order)
  const getOrderedContactKeys = () => {
    const allKeys = getAllContactKeys();
    
    if (contactOrder.length === 0) {
      return allKeys;
    }
    
    // Sort based on the stored order
    const orderedKeys = [];
    const keySet = new Set(allKeys);
    
    // Add contacts in the specified order
    contactOrder.forEach(key => {
      if (keySet.has(key)) {
        orderedKeys.push(key);
        keySet.delete(key);
      }
    });
    
    // Add any remaining contacts that weren't in the order (newly added)
    keySet.forEach(key => {
      orderedKeys.push(key);
    });
    
    return orderedKeys;
  };

  // Get ordered selected contacts (respects drag-and-drop order)
  const getOrderedSelectedContacts = () => {
    const contacts = getSelectedContacts();
    
    if (contactOrder.length === 0) {
      // If no custom order set, return default order
      return contacts;
    }
    
    // Sort contacts based on the stored order
    const orderedContacts = [];
    const contactMap = new Map(contacts.map(contact => [contact.id, contact]));
    
    // Add contacts in the specified order
    contactOrder.forEach(id => {
      if (contactMap.has(id)) {
        orderedContacts.push(contactMap.get(id));
        contactMap.delete(id);
      }
    });
    
    // Add any remaining contacts that weren't in the order (newly added)
    contactMap.forEach(contact => {
      orderedContacts.push(contact);
    });
    
    return orderedContacts;
  };

  // Render a single contact (original or injected)
  const renderContact = (contactKey, index, isInjected = false, originalContact = null) => {
    let contact, data, isSelected, category;
    
    if (isInjected) {
      // This is an injected contact
      const [originalKey] = contactKey.split('-injected-');
      const injectedContact = injectedContacts[originalKey]?.find(c => c.injectedKey === contactKey);
      if (!injectedContact) return null;
      
      contact = injectedContact;
      data = {}; // Injected contacts store their data directly
      isSelected = isContactSelected(contactKey);
      category = originalKey.split('-')[0]; // Extract category from original key
    } else {
      // This is an original contact
      const [cat, contactIndex] = contactKey.split('-');
      category = cat;
      const categoryContacts = masterContacts[category];
      if (!categoryContacts || !categoryContacts[contactIndex]) return null;
      
      contact = categoryContacts[contactIndex];
      data = contactData[contactKey] || {};
      isSelected = isContactSelected(contactKey);
    }
    
    const isCollapsed = isContactCollapsed(contactKey);

    return (
      <TaskCard
        task={contact}
        taskKey={contactKey}
        category={category}
        index={index}
        isSelected={isSelected}
        isCollapsed={isCollapsed}
        isInjected={isInjected}
        isCustom={false}
        customName={isInjected ? contact.customName : data.customName}
        instructions={isInjected ? contact.address : data.address || contact.address}
        tools={isInjected ? contact.tools : data.tools}
        notes={isInjected ? contact.notes : data.notes}
        onToggle={(checked) => handleContactToggle(contactKey, checked)}
        onCollapse={() => toggleContactCollapse(contactKey)}
        onDataChange={(field, value) => handleContactDataChange(contactKey, field, value)}
        onAddAnother={!isInjected ? () => {
          const [cat, contactIndex] = contactKey.split('-');
          const originalContact = masterContacts[cat][contactIndex];
          addAnotherContact(cat, originalContact, parseInt(contactIndex));
        } : null}
        onRemove={isInjected ? () => {
          const [originalKey] = contactKey.split('-injected-');
          removeInjectedContact(originalKey, contactKey);
        } : null}
        dragHandlers={{
          onDragStart: (e) => handleDragStart(e, contactKey, index),
          onDragEnd: handleDragEnd,
          onDragOver: handleDragOver,
          onDrop: (e) => handleDrop(e, contactKey, index)
        }}
        originalContact={originalContact}
      />
    );
  };



  const handleCustomContactToggle = (contactId, checked) => {
    setSelectedContacts(prev => {
      const newSelected = new Set(prev);
      if (checked) {
        newSelected.add(contactId);
      } else {
        newSelected.delete(contactId);
      }
      return newSelected;
    });
  };

  const getSelectedContacts = () => {
    const selectedContactsList = [];
    
    // Add selected master contacts
    Object.entries(masterContacts).forEach(([category, contacts]) => {
      contacts.forEach((contact, index) => {
        const contactKey = `${category}-${index}`;
        
        if (selectedContacts.has(contactKey)) {
          const data = contactData[contactKey] || {};
          selectedContactsList.push({
            id: contactKey,
            name: data.customName || contact.name, // Use custom name if provided
            originalName: contact.name, // Keep original for reference
            address: data.address || contact.address || "",
            tools: data.tools || "",
            category,
            subcategory: contact.subcategory || "",
            is_custom: false,
            notes: data.notes || ""
          });
        }
        
        // Add selected injected contacts for this original contact
        const injectedForThisContact = injectedContacts[contactKey] || [];
        injectedForThisContact.forEach(injectedContact => {
          if (selectedContacts.has(injectedContact.injectedKey)) {
            selectedContactsList.push({
              id: injectedContact.injectedKey,
              name: injectedContact.customName || injectedContact.name,
              originalName: injectedContact.name,
              address: injectedContact.address || "",
              tools: injectedContact.tools || "",
              category,
              subcategory: contact.subcategory || "",
              is_custom: true,
              is_injected: true,
              notes: injectedContact.notes || ""
            });
          }
        });
      });
    });
    
    // Add selected custom contacts
    customContacts.forEach(contact => {
      if (selectedContacts.has(contact.id)) {
        selectedContactsList.push(contact);
      }
    });
    
    return selectedContactsList;
  };

  const exportMaintenanceSchedule = () => {
    const selectedTasksList = getSelectedContacts();
    
    if (selectedTasksList.length === 0) {
      toast.error("Please select some tasks before exporting");
      return;
    }

    const exportData = {
      user_name: userName,
      contacts: selectedTasksList,
      selected_contacts: Array.from(selectedContacts),
      contact_data: contactData,
      template_assignments: templateAssignments,
      custom_contacts: customContacts,
      custom_category_icons: customCategoryIcons,
      injected_contacts: injectedContacts,
      contact_order: contactOrder,
      category_order: categoryOrder,
      collapsed_contacts: Array.from(collapsedContacts),
      collapsed_categories: Array.from(collapsedCategories),
      collapsed_custom_contacts: Array.from(collapsedCustomContacts),
      exported_at: new Date().toISOString(),
      app_version: "1.0",
      app_type: "home_maintenance"
    };

    const dataStr = JSON.stringify(exportData, null, 2);
    const dataUri = 'data:application/json;charset=utf-8,'+ encodeURIComponent(dataStr);
    
    const exportFileDefaultName = `home-maintenance-${userName || 'schedule'}-${new Date().toISOString().split('T')[0]}.json`;
    
    const linkElement = document.createElement('a');
    linkElement.setAttribute('href', dataUri);
    linkElement.setAttribute('download', exportFileDefaultName);
    linkElement.click();
    
    toast.success("Maintenance schedule exported successfully");
  };

  // Reset all data to start fresh
  const resetAllData = () => {
    setSelectedContacts(new Set());
    setContactData({});
    setTemplateAssignments({
      travel: new Set(),
      wallet: new Set()
    });
    setCustomContacts([]);
    setCustomCategoryIcons({});
    setInjectedContacts({});
    setContactOrder([]);
    setCategoryOrder([]);
    setCollapsedContacts(new Set());
    setCollapsedCategories(new Set());
    setCollapsedCustomContacts(new Set());
    setUserName("");
    setHighlightedItems(new Set());
    
    toast.success("All data cleared successfully");
  };

  const importMaintenanceSchedule = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const importData = JSON.parse(e.target.result);
        
        console.log("Import data received:", importData);
        
        // Validate that this is a maintenance schedule export
        if (!importData.selected_contacts && !importData.contact_data && !importData.user_name) {
          throw new Error("Invalid maintenance schedule format");
        }
        
        // Import basic data
        if (importData.user_name) {
          setUserName(importData.user_name);
          console.log("Imported user name:", importData.user_name);
        }
        
        if (importData.contact_data) {
          setContactData(importData.contact_data);
          console.log("Imported contact data:", Object.keys(importData.contact_data).length, "contacts");
        }
        
        if (importData.custom_contacts) {
          setCustomContacts(importData.custom_contacts);
          console.log("Imported custom contacts:", importData.custom_contacts.length);
        }
        
        if (importData.template_assignments) {
          setTemplateAssignments(importData.template_assignments);
          console.log("Imported template assignments:", importData.template_assignments);
        }
        
        if (importData.selected_contacts) {
          const selectedSet = new Set(importData.selected_contacts);
          setSelectedContacts(selectedSet);
          console.log("Imported selected contacts:", selectedSet.size);
        }
        
        // Import extended state (new fields)
        if (importData.custom_category_icons) {
          setCustomCategoryIcons(importData.custom_category_icons);
          console.log("Imported custom category icons:", Object.keys(importData.custom_category_icons).length);
        }
        
        if (importData.injected_contacts) {
          setInjectedContacts(importData.injected_contacts);
          console.log("Imported injected contacts:", Object.keys(importData.injected_contacts).length);
        }
        
        if (importData.contact_order) {
          setContactOrder(importData.contact_order);
          console.log("Imported contact order:", importData.contact_order.length);
        }
        
        if (importData.category_order) {
          setCategoryOrder(importData.category_order);
          console.log("Imported category order:", importData.category_order.length);
        }
        
        if (importData.collapsed_contacts) {
          setCollapsedContacts(new Set(importData.collapsed_contacts));
          console.log("Imported collapsed contacts:", importData.collapsed_contacts.length);
        }
        
        if (importData.collapsed_categories) {
          setCollapsedCategories(new Set(importData.collapsed_categories));
          console.log("Imported collapsed categories:", importData.collapsed_categories.length);
        }
        
        if (importData.collapsed_custom_contacts) {
          setCollapsedCustomContacts(new Set(importData.collapsed_custom_contacts));
          console.log("Imported collapsed custom contacts:", importData.collapsed_custom_contacts.length);
        }
        
        // Clear any highlighting
        setHighlightedItems(new Set());
        
        toast.success(`Maintenance schedule imported successfully! Loaded ${importData.selected_contacts ? importData.selected_contacts.length : 0} tasks.`);
        
      } catch (error) {
        console.error("Import error:", error);
        toast.error(`Import failed: ${error.message}`);
      }
    };
    
    reader.onerror = (error) => {
      console.error("File read error:", error);
      toast.error("Failed to read file");
    };
    
    reader.readAsText(file);
    
    // Reset file input so same file can be imported again
    event.target.value = '';
  };

  const printMaintenanceSchedule = (mode) => {
    const selectedContactsList = getSelectedContacts();
    
    if (selectedContactsList.length === 0) {
      toast.error("Please select some tasks before printing");
      return;
    }
    
    console.log("Setting print mode to:", mode);
    console.log("Selected tasks:", selectedContactsList.length);
    setPrintMode(mode);
    
    // Show template for 5 seconds, then auto-print
    setTimeout(() => {
      console.log("Print mode active, template should be visible");
      // User can manually print when ready (Ctrl+P)
      toast.info("Template ready! Use Ctrl+P to print, or wait 3 seconds for auto-print", {
        duration: 3000
      });
      
      // Auto-print after delay
      setTimeout(() => {
        window.print();
        
        // Reset after printing
        setTimeout(() => {
          setPrintMode("");
        }, 1000);
      }, 3000);
    }, 500);
  };

  // Preview template function for testing
  const previewTemplate = (mode) => {
    const selectedContactsList = getSelectedContacts();
    
    if (selectedContactsList.length === 0) {
      toast.error("Please select some tasks before previewing");
      return;
    }
    
    console.log("Setting preview mode to:", mode);
    setPrintMode(mode);
    
    // Don't call print, just show the template
    setTimeout(() => {
      console.log("Template preview active");
    }, 200);
  };

  const getContactsByTemplate = (template) => {
    const orderedContacts = getOrderedSelectedContacts();
    
    // All templates now include all selected tasks - no filtering needed
    return orderedContacts;
  };

  const getSelectedTasksCount = () => {
    return selectedContacts.size;
  };

  // Gamification helper functions
  const getTaskCompletionStats = () => {
    const selectedTasksList = getSelectedContacts();
    const stats = {
      total: selectedTasksList.length,
      withInstructions: 0,
      withTools: 0,
      withBoth: 0,
      fullyComplete: 0, // Both instructions and tools
      byCategory: {}
    };

    selectedTasksList.forEach(task => {
      const hasInstructions = task.address && task.address.trim() !== '';
      const hasTools = task.tools && task.tools.trim() !== '';
      
      if (hasInstructions) stats.withInstructions++;
      if (hasTools) stats.withTools++;
      if (hasInstructions && hasTools) stats.withBoth++;
      if (hasInstructions && hasTools) stats.fullyComplete++;

      // Count by category
      const category = task.category || 'Uncategorized';
      if (!stats.byCategory[category]) {
        stats.byCategory[category] = { total: 0, withInstructions: 0, withTools: 0, complete: 0 };
      }
      stats.byCategory[category].total++;
      if (hasInstructions) {
        stats.byCategory[category].withInstructions++;
      }
      if (hasTools) {
        stats.byCategory[category].withTools++;
      }
      if (hasInstructions && hasTools) {
        stats.byCategory[category].complete++;
      }
    });

    return stats;
  };

  const calculateMaintenanceReadinessScore = () => {
    const stats = getTaskCompletionStats();
    if (stats.total === 0) return 0;
    
    // Base score from full task completion (60% weight - instructions + tools)
    const fullCompletionScore = (stats.fullyComplete / stats.total) * 60;
    
    // Partial completion score (20% weight - having either instructions OR tools)
    const partialCompletion = Math.max(stats.withInstructions, stats.withTools) - stats.fullyComplete;
    const partialScore = (partialCompletion / stats.total) * 20;
    
    // Monthly coverage score (20% weight)
    const monthsWithTasks = Object.keys(stats.byCategory).length;
    const maxMonths = Object.keys(masterContacts).length || 12;
    const monthlyScore = (monthsWithTasks / maxMonths) * 20;
    
    return Math.round(fullCompletionScore + partialScore + monthlyScore);
  };

  // Readiness score animation on change
  const currentReadinessScore = calculateMaintenanceReadinessScore();
  useEffect(() => {
    if (prevReadinessScore.current !== null && currentReadinessScore > prevReadinessScore.current) {
      setScoreAnimating(true);
      setTimeout(() => setScoreAnimating(false), 400);
      // Milestone toasts
      const milestones = [25, 50, 75, 100];
      for (const m of milestones) {
        if (currentReadinessScore >= m && prevReadinessScore.current < m) {
          toast.success(`Readiness ${m}%! ${m === 100 ? 'Your maintenance plan is fully complete!' : 'Keep going!'}`);
          break;
        }
      }
    }
    prevReadinessScore.current = currentReadinessScore;
  }, [currentReadinessScore]);

  if (printMode) {
    const tasksForPrint = getContactsByTemplate(printMode);
    
    if (printMode === 'master') {
      // Master Maintenance Schedule - Complete planning reference
      const tasksByCategory = tasksForPrint.reduce((groups, task) => {
        const category = task.category || 'Other';
        if (!groups[category]) groups[category] = [];
        groups[category].push(task);
        return groups;
      }, {});

      const totalTasks = tasksForPrint.length;
      const monthsWithTasks = Object.keys(tasksByCategory).length;

      return (
        <div className={`print-template ${printMode}-template`}>
          {/* Cover Page */}
          <div className="cover-page">
            <div className="cover-header">
              <h1>🏠 Master Home Maintenance Schedule</h1>
              <div className="cover-subtitle">
                <p>Your Complete Home Care Planning Guide</p>
                <p>{userName ? `Prepared for: ${userName}` : 'Personal Home Maintenance Plan'}</p>
                <p>Generated: {new Date().toLocaleDateString()}</p>
              </div>
            </div>
            
            <div className="cover-content">
              <div className="schedule-overview">
                <h2>📊 Schedule Overview</h2>
                <div className="overview-stats">
                  <div className="stat-item">
                    <span className="stat-number">{totalTasks}</span>
                    <span className="stat-label">Total Maintenance Tasks</span>
                  </div>
                  <div className="stat-item">
                    <span className="stat-number">{monthsWithTasks}</span>
                    <span className="stat-label">Months Covered</span>
                  </div>
                  <div className="stat-item">
                    <span className="stat-number">{calculateMaintenanceReadinessScore()}%</span>
                    <span className="stat-label">Schedule Completion</span>
                  </div>
                </div>
              </div>

              <div className="usage-instructions">
                <h2>📋 How to Use This Schedule</h2>
                <div className="instructions-grid">
                  <div className="instruction-item">
                    <div className="instruction-number">1</div>
                    <div className="instruction-content">
                      <h3>📖 Planning Phase</h3>
                      <p>Use this Master Schedule for what, when, and how</p>
                    </div>
                  </div>
                  <div className="instruction-item">
                    <div className="instruction-number">2</div>
                    <div className="instruction-content">
                      <h3>✅ Working Phase</h3>
                      <p>Use the Task Checklist to track progress while working</p>
                    </div>
                  </div>
                  <div className="instruction-item">
                    <div className="instruction-number">3</div>
                    <div className="instruction-content">
                      <h3>📝 Recording Phase</h3>
                      <p>Document completion in the Maintenance Log for records</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="maintenance-tips">
                <h2>💡 Essential Maintenance Tips</h2>
                <div className="tips-grid">
                  <div className="tip-section">
                    <h3>🛠️ Before You Start</h3>
                    <ul>
                      <li>Review instructions for each task</li>
                      <li>Gather all required tools and supplies</li>
                      <li>Check weather conditions for outdoor tasks</li>
                      <li>Schedule tasks during optimal times</li>
                    </ul>
                  </div>
                  <div className="tip-section">
                    <h3>📅 Scheduling Guidelines</h3>
                    <ul>
                      <li>Monthly: First weekend of each month</li>
                      <li>Seasonal: Start of season</li>
                      <li>Annual: Spring or fall</li>
                      <li>Emergency: As needed immediately</li>
                    </ul>
                  </div>
                  <div className="tip-section">
                    <h3>🔧 Tool Organization</h3>
                    <ul>
                      <li>Keep basic tools easily accessible</li>
                      <li>Check tool condition before use</li>
                      <li>Purchase supplies in advance</li>
                      <li>Have backup supplies for critical items</li>
                    </ul>
                  </div>
                  <div className="tip-section">
                    <h3>📝 Record Keeping</h3>
                    <ul>
                      <li>Document completion dates</li>
                      <li>Note any issues discovered</li>
                      <li>Track recurring problems</li>
                      <li>Keep receipts for warranty claims</li>
                    </ul>
                  </div>
                </div>
              </div>

              <div className="cover-footer">
                <p><strong>Your Year-Round Home Care System</strong></p>
                <p>This comprehensive maintenance schedule helps you protect your investment and ensure your home stays in optimal condition throughout the year.</p>
              </div>
            </div>
          </div>
          
          {/* Task Lists - Separate from Cover Page */}
          <div className="print-contacts-grouped" style={{pageBreakBefore: 'always'}}>
            {Object.entries(tasksByCategory).map(([month, tasks]) => (
              <div key={month} className="print-category-section">
                <div className="print-category-header">
                  <div className="print-category-icon">📅</div>
                  <h2 className="print-category-title">{month}</h2>
                  <div className="print-category-count">({tasks.length} task{tasks.length !== 1 ? 's' : ''})</div>
                </div>
                
                <div className="print-contacts">
                  {tasks.map((task, index) => (
                    <div key={task.id || index} className="print-contact">
                      <div className="contact-name">{task.name}</div>
                      {task.address && <div className="contact-phone">📋 {task.address}</div>}
                      {task.tools && <div className="contact-address">🔧 {task.tools}</div>}
                      {task.notes && <div className="contact-notes">💡 {task.notes}</div>}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      );
    }
    
    if (printMode === 'checklist') {
      // Task Checklist - Simple checkboxes for while working
      const tasksByCategory = tasksForPrint.reduce((groups, task) => {
        const category = task.category || 'Other';
        if (!groups[category]) groups[category] = [];
        groups[category].push(task);
        return groups;
      }, {});

      const totalTasks = tasksForPrint.length;
      const monthsWithTasks = Object.keys(tasksByCategory).length;

      return (
        <div className={`print-template ${printMode}-template`}>
          {/* Cover Page */}
          <div className="cover-page">
            <div className="cover-header">
              <h1>✅ Maintenance Task Checklist</h1>
              <div className="cover-subtitle">
                <p>Your Working Checklist for Task Completion</p>
                <p>{userName ? `Prepared for: ${userName}` : 'Personal Task Tracking'}</p>
                <p>Generated: {new Date().toLocaleDateString()}</p>
              </div>
            </div>
            
            <div className="cover-content">
              <div className="schedule-overview">
                <h2>📋 Checklist Overview</h2>
                <div className="overview-stats">
                  <div className="stat-item">
                    <span className="stat-number">{totalTasks}</span>
                    <span className="stat-label">Tasks to Complete</span>
                  </div>
                  <div className="stat-item">
                    <span className="stat-number">{monthsWithTasks}</span>
                    <span className="stat-label">Months Active</span>
                  </div>
                  <div className="stat-item">
                    <span className="stat-number">✓</span>
                    <span className="stat-label">Track Progress</span>
                  </div>
                </div>
              </div>

              <div className="usage-instructions">
                <h2>🎯 How to Use This Checklist</h2>
                <div className="instructions-grid">
                  <div className="instruction-item">
                    <div className="instruction-number">1</div>
                    <div className="instruction-content">
                      <h3>📅 Plan Your Work</h3>
                      <p>Review tasks for the current month and prioritize based on weather and availability</p>
                    </div>
                  </div>
                  <div className="instruction-item">
                    <div className="instruction-number">2</div>
                    <div className="instruction-content">
                      <h3>🔧 Gather Tools</h3>
                      <p>Collect all required tools and materials before starting each task</p>
                    </div>
                  </div>
                  <div className="instruction-item">
                    <div className="instruction-number">3</div>
                    <div className="instruction-content">
                      <h3>✅ Track Completion</h3>
                      <p>Check off tasks as completed and note the completion date</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="maintenance-tips">
                <h2>⚡ Working Efficiently</h2>
                <div className="tips-grid">
                  <div className="tip-section">
                    <h3>🌤️ Weather Considerations</h3>
                    <ul>
                      <li>Check weather before outdoor tasks</li>
                      <li>Plan indoor tasks for rainy days</li>
                      <li>Avoid extreme temperature days</li>
                      <li>Consider seasonal timing</li>
                    </ul>
                  </div>
                  <div className="tip-section">
                    <h3>🔧 Tool Preparation</h3>
                    <ul>
                      <li>Check tool condition before use</li>
                      <li>Have spare batteries ready</li>
                      <li>Keep safety equipment accessible</li>
                      <li>Organize tools by task type</li>
                    </ul>
                  </div>
                  <div className="tip-section">
                    <h3>⏰ Time Management</h3>
                    <ul>
                      <li>Allow extra time for complex tasks</li>
                      <li>Group similar tasks together</li>
                      <li>Take breaks for safety</li>
                      <li>Don't rush important work</li>
                    </ul>
                  </div>
                  <div className="tip-section">
                    <h3>🔍 Quality Control</h3>
                    <ul>
                      <li>Double-check completed work</li>
                      <li>Test systems after maintenance</li>
                      <li>Clean up work area when done</li>
                      <li>Note any issues discovered</li>
                    </ul>
                  </div>
                </div>
              </div>

              <div className="cover-footer">
                <p><strong>Your Working Companion</strong></p>
                <p>Use this checklist in the field to stay organized and ensure nothing is missed during your maintenance work.</p>
              </div>
            </div>
          </div>
          
          {/* Task Checklist - Separate from Cover Page */}
          <div className="print-contacts-grouped" style={{pageBreakBefore: 'always'}}>
            {Object.entries(tasksByCategory).map(([month, tasks]) => (
              <div key={month} className="print-category-section">
                <div className="print-category-header">
                  <div className="print-category-icon">📅</div>
                  <h2 className="print-category-title">{month}</h2>
                  <div className="print-category-count">({tasks.length} task{tasks.length !== 1 ? 's' : ''})</div>
                </div>
                
                <div className="print-contacts">
                  {tasks.map((task, index) => (
                    <div key={task.id || index} className="print-contact">
                      <div className="contact-name">☐ {task.name}</div>
                      {task.address && <div className="contact-phone">📋 {task.address}</div>}
                      <div className="contact-address">📅 Completed: __________</div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
          
          <div className="checklist-footer">
            <p>📋 Check off tasks as completed • ✅ = Task Complete • 📅 = Record completion date</p>
          </div>
        </div>
      );
    }
    
    if (printMode === 'log') {
      // Maintenance Log - Documentation and completion tracking
      const tasksByCategory = tasksForPrint.reduce((groups, task) => {
        const category = task.category || 'Other';
        if (!groups[category]) groups[category] = [];
        groups[category].push(task);
        return groups;
      }, {});

      const totalTasks = tasksForPrint.length;
      const monthsWithTasks = Object.keys(tasksByCategory).length;

      return (
        <div className={`print-template ${printMode}-template`}>
          {/* Cover Page */}
          <div className="cover-page">
            <div className="cover-header">
              <h1>📝 Home Maintenance Activity Log</h1>
              <div className="cover-subtitle">
                <p>Professional Record Keeping & Documentation</p>
                <p>{userName ? `Property Owner: ${userName}` : 'Home Maintenance Records'}</p>
                <p>Log Period: {new Date().getFullYear()} • Generated: {new Date().toLocaleDateString()}</p>
              </div>
            </div>
            
            <div className="cover-content">
              <div className="schedule-overview">
                <h2>📊 Documentation Overview</h2>
                <div className="overview-stats">
                  <div className="stat-item">
                    <span className="stat-number">{totalTasks}</span>
                    <span className="stat-label">Tasks to Document</span>
                  </div>
                  <div className="stat-item">
                    <span className="stat-number">{monthsWithTasks}</span>
                    <span className="stat-label">Active Months</span>
                  </div>
                  <div className="stat-item">
                    <span className="stat-number">📋</span>
                    <span className="stat-label">Record Everything</span>
                  </div>
                </div>
              </div>

              <div className="usage-instructions">
                <h2>📋 Documentation Process</h2>
                <div className="instructions-grid">
                  <div className="instruction-item">
                    <div className="instruction-number">1</div>
                    <div className="instruction-content">
                      <h3>✅ Complete Tasks</h3>
                      <p>Finish maintenance work following your checklist and schedule</p>
                    </div>
                  </div>
                  <div className="instruction-item">
                    <div className="instruction-number">2</div>
                    <div className="instruction-content">
                      <h3>📝 Document Immediately</h3>
                      <p>Record completion details while the work is fresh in your memory</p>
                    </div>
                  </div>
                  <div className="instruction-item">
                    <div className="instruction-number">3</div>
                    <div className="instruction-content">
                      <h3>💾 Archive Records</h3>
                      <p>Store completed logs for warranty claims, sales, and future reference</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="maintenance-tips">
                <h2>📖 Record Keeping Best Practices</h2>
                <div className="tips-grid">
                  <div className="tip-section">
                    <h3>📅 Timing</h3>
                    <ul>
                      <li>Document immediately after completion</li>
                      <li>Include specific dates and times</li>
                      <li>Note weather conditions if relevant</li>
                      <li>Record seasonal observations</li>
                    </ul>
                  </div>
                  <div className="tip-section">
                    <h3>💰 Cost Tracking</h3>
                    <ul>
                      <li>Record all expenses, including labor</li>
                      <li>Keep receipts attached or referenced</li>
                      <li>Note warranty information</li>
                      <li>Track cost trends over time</li>
                    </ul>
                  </div>
                  <div className="tip-section">
                    <h3>🔍 Observations</h3>
                    <ul>
                      <li>Note any issues discovered</li>
                      <li>Document unusual wear or damage</li>
                      <li>Record system performance changes</li>
                      <li>Identify recurring problems</li>
                    </ul>
                  </div>
                  <div className="tip-section">
                    <h3>📋 Documentation</h3>
                    <ul>
                      <li>Be specific and detailed</li>
                      <li>Use clear, professional language</li>
                      <li>Include photos when helpful</li>
                      <li>Plan next maintenance cycles</li>
                    </ul>
                  </div>
                </div>
              </div>

              <div className="cover-footer">
                <p><strong>Your Professional Home Care Record</strong></p>
                <p>This log provides documentation for warranty claims, insurance purposes, home sales, and future maintenance planning.</p>
              </div>
            </div>
          </div>
          
          {/* Maintenance Log - Separate from Cover Page */}
          <div className="print-contacts-grouped" style={{pageBreakBefore: 'always'}}>
            {Object.entries(tasksByCategory).map(([month, tasks]) => (
              <div key={month} className="print-category-section">
                <div className="print-category-header">
                  <div className="print-category-icon">📅</div>
                  <h2 className="print-category-title">{month}</h2>
                  <div className="print-category-count">({tasks.length} task{tasks.length !== 1 ? 's' : ''})</div>
                </div>
                
                <div className="print-contacts">
                  {tasks.map((task, index) => (
                    <div key={task.id || index} className="print-contact">
                      <div className="contact-name">{task.name}</div>
                      {task.address && <div className="contact-phone">📋 {task.address}</div>}
                      <div className="contact-address">📅 Completed: __________ Cost: $ ______</div>
                      <div className="contact-notes">💡 Notes: _________________________________</div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      );
    }
  }

  return (
    <ToolLayout
      title="Home Maintenance List Generator"
      description="Keep track of home maintenance tasks month by month"
      icon={FileText}
      guide={{
        intro: 'Stay on top of home maintenance with a month-by-month schedule.',
        steps: [
          'View tasks organized by month.',
          'Check off tasks as you complete them.',
          'Add your own custom tasks.',
          'Print your schedule for reference.',
        ],
      }}
    >
      <>
        {/* User Info & Actions */}
        <Card className="mb-8" data-print-visible={printMode ? "true" : "false"}>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              📋 Maintenance Schedule Setup
            </CardTitle>
          </CardHeader>
          <CardContent>
              <div className="flex-1">
                <Label htmlFor="userName">Your Name (Optional)</Label>
                <Input
                  id="userName"
                  value={userName}
                  onChange={(e) => setUserName(e.target.value)}
                  placeholder="Enter your name for the maintenance schedule"
                  className="mt-1"
                />
              </div>

              <div className="flex gap-2 justify-end mt-3">
                <Button
                  onClick={exportMaintenanceSchedule}
                  className="flex items-center gap-2 bg-primary text-primary-foreground rounded-lg hover:opacity-90"
                >
                  <Download className="h-4 w-4" />
                  Save
                </Button>
                <Label htmlFor="importFile" className="cursor-pointer">
                  <Button className="flex items-center gap-2 bg-primary text-primary-foreground rounded-lg hover:opacity-90" asChild>
                    <span>
                      <Upload className="h-4 w-4" />
                      Load
                    </span>
                  </Button>
                  <Input
                    id="importFile"
                    type="file"
                    accept=".json"
                    onChange={importMaintenanceSchedule}
                    className="hidden"
                  />
                </Label>
                <Button
                  onClick={resetAllData}
                  variant="outline"
                  className="flex items-center gap-2 text-red-600 hover:text-red-700 hover:bg-red-50"
                >
                  <X className="h-4 w-4" />
                  Clear All
                </Button>
              </div>
          </CardContent>
        </Card>

        {/* Main Content */}
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Task Selection */}
          <div className="lg:col-span-2">
            <Card>
              <CardHeader>
                <CardTitle>Home Maintenance Tasks</CardTitle>
                <CardDescription>
                  Select and customize maintenance tasks for your home. Add task details and instructions to create your personalized maintenance schedule.
                </CardDescription>
              </CardHeader>
              <CardContent>
                {/* Action Buttons - Top */}
                <div className="flex gap-2 mb-6">
                  <Dialog open={showCustomContactDialog} onOpenChange={setShowCustomContactDialog}>
                    <DialogTrigger asChild>
                      <Button className="flex-1 flex items-center gap-2">
                        <Plus className="h-4 w-4" />
                        Add Custom Task
                      </Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle>Add Custom Task</DialogTitle>
                        <DialogDescription>
                          Add a maintenance task that's not in our master list
                        </DialogDescription>
                      </DialogHeader>
                      <div className="space-y-4">
                        <div>
                          <Label htmlFor="customName">Task Name *</Label>
                          <Input
                            id="customName"
                            value={newCustomContact.name}
                            onChange={(e) => setNewCustomContact(prev => 
                              ({ ...prev, name: e.target.value }))}
                            placeholder="Enter maintenance task name"
                          />
                        </div>
                        <div>
                          <Label htmlFor="customAddress">Instructions</Label>
                          <Input
                            id="customAddress"
                            value={newCustomContact.address}
                            onChange={(e) => setNewCustomContact(prev => 
                              ({ ...prev, address: e.target.value }))}
                            placeholder="Enter step-by-step instructions"
                          />
                        </div>
                        <div>
                          <Label htmlFor="customTools">Required Tools & Supplies</Label>
                          <Textarea
                            id="customTools"
                            value={newCustomContact.tools}
                            onChange={(e) => setNewCustomContact(prev => 
                              ({ ...prev, tools: e.target.value }))}
                            placeholder="List required tools, materials, supplies (e.g., screwdriver, filter, cleaner)"
                            rows={2}
                          />
                        </div>
                        <div>
                          <Label htmlFor="customCategory">Maintenance Category</Label>
                          <div className="relative category-dropdown-container">
                            <div className="relative">
                              <Input
                                id="customCategory"
                                value={newCustomContact.category}
                                onChange={(e) => setNewCustomContact(prev => 
                                  ({ ...prev, category: e.target.value }))}
                                placeholder="Select existing category or enter custom"
                                className="pr-10"
                              />
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                className="absolute right-0 top-0 h-full px-3 hover:bg-transparent"
                                onClick={() => setShowCategoryDropdown(!showCategoryDropdown)}
                              >
                                <ChevronDown className="h-4 w-4" />
                              </Button>
                            </div>
                            
                            {showCategoryDropdown && (
                              <div className="absolute z-50 w-full mt-1 bg-popover border border-border rounded-md shadow-lg max-h-60 overflow-y-auto text-popover-foreground">
                                <div className="p-2">
                                  <div className="text-xs font-medium text-gray-500 mb-2">Existing Categories:</div>
                                  {Object.keys(masterContacts).map((category) => (
                                    <button
                                      key={category}
                                      type="button"
                                      className="w-full text-left px-3 py-2 text-sm hover:bg-gray-100 rounded flex items-center gap-2"
                                      onClick={() => {
                                        setNewCustomContact(prev => ({ ...prev, category }));
                                        setShowCategoryDropdown(false);
                                      }}
                                    >
                                      {React.createElement(getCategoryIcon(category), { 
                                        className: "h-4 w-4 text-blue-600" 
                                      })}
                                      {category}
                                    </button>
                                  ))}
                                  <div className="border-t mt-2 pt-2">
                                    <div className="text-xs font-medium text-gray-500 mb-2">Popular Custom Categories:</div>
                                    {['Personal Contacts', 'Work Emergency', 'Neighborhood', 'Extended Family'].map((category) => (
                                      <button
                                        key={category}
                                        type="button"
                                        className="w-full text-left px-3 py-2 text-sm hover:bg-gray-100 rounded text-blue-600 flex items-center gap-2"
                                        onClick={() => {
                                          setNewCustomContact(prev => ({ ...prev, category }));
                                          setShowCategoryDropdown(false);
                                        }}
                                      >
                                        {React.createElement(getCategoryIcon(category), { 
                                          className: "h-4 w-4 text-blue-600" 
                                        })}
                                        {category}
                                      </button>
                                    ))}
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                          <p className="text-xs text-gray-500 mt-1">
                            Choose from existing categories or type your own
                          </p>
                        </div>
                        <div>
                          <Label htmlFor="customNotes">Notes (Optional)</Label>
                          <Textarea
                            id="customNotes"
                            value={newCustomContact.notes}
                            onChange={(e) => setNewCustomContact(prev => 
                              ({ ...prev, notes: e.target.value }))}
                            placeholder="Additional information"
                            rows={2}
                          />
                        </div>
                        <div className="flex justify-end gap-2">
                          <Button
                            variant="outline"
                            onClick={() => setShowCustomContactDialog(false)}
                          >
                            Cancel
                          </Button>
                          <Button onClick={addCustomContact}>
                            Add Contact
                          </Button>
                        </div>
                      </div>
                    </DialogContent>
                  </Dialog>

                  <Dialog open={showCustomCategoryDialog} onOpenChange={(open) => {
                    setShowCustomCategoryDialog(open);
                    if (!open) {
                      setShowEmojiPicker(false);
                      setNewCustomCategory({ name: "", icon: "" });
                    }
                  }}>
                    <DialogTrigger asChild>
                      <Button variant="outline" className="flex-1 flex items-center gap-2">
                        <Plus className="h-4 w-4" />
                        Add Custom Category
                      </Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle>Add Custom Category</DialogTitle>
                        <DialogDescription>
                          Create a new category for organizing your contacts
                        </DialogDescription>
                      </DialogHeader>
                      <div className="space-y-4">
                        <div>
                          <Label htmlFor="categoryName">Category Name *</Label>
                          <Input
                            id="categoryName"
                            value={newCustomCategory.name}
                            onChange={(e) => setNewCustomCategory(prev => 
                              ({ ...prev, name: e.target.value }))}
                            placeholder="Enter category name"
                          />
                        </div>
                        <div>
                          <Label htmlFor="categoryIcon">Category Icon/Emoji</Label>
                          <div className="flex gap-2">
                            <Input
                              id="categoryIcon"
                              value={newCustomCategory.icon}
                              onChange={(e) => setNewCustomCategory(prev => 
                                ({ ...prev, icon: e.target.value }))}
                              placeholder="Enter emoji (e.g., 🏥, 💼, 👨‍⚕️)"
                              className="flex-1"
                            />
                            <div className="relative">
                              <Button
                                type="button"
                                variant="outline"
                                size="icon"
                                onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                                className="shrink-0"
                              >
                                <Smile className="h-4 w-4" />
                              </Button>
                              
                              {showEmojiPicker && (
                                <>
                                  <div 
                                    className="fixed inset-0 z-40" 
                                    onClick={() => setShowEmojiPicker(false)}
                                  />
                                  <div className="absolute top-12 right-0 z-50 shadow-lg rounded-lg border border-border bg-popover text-popover-foreground emoji-picker-wrapper">
                                    <EmojiPicker
                                      onEmojiClick={onEmojiClick}
                                      autoFocusSearch={false}
                                      theme="light"
                                      categories={[
                                        'symbols',
                                        'smileys_people',
                                        'animals_nature',
                                        'food_drink',
                                        'travel_places',
                                        'activities',
                                        'objects',
                                        'flags'
                                      ]}
                                      width={300}
                                      height={350}
                                      skinTonesDisabled
                                    />
                                  </div>
                                </>
                              )}
                            </div>
                          </div>
                          <p className="text-xs text-gray-500 mt-1">
                            Type an emoji or click the smile button to choose from picker
                          </p>
                        </div>
                        <div className="flex justify-end gap-2">
                          <Button
                            variant="outline"
                            onClick={() => {
                              setShowCustomCategoryDialog(false);
                              setShowEmojiPicker(false);
                              setNewCustomCategory({ name: "", icon: "" });
                            }}
                          >
                            Cancel
                          </Button>
                          <Button onClick={addCustomCategory}>
                            Add Category
                          </Button>
                        </div>
                      </div>
                    </DialogContent>
                  </Dialog>
                </div>

                {/* Unified Contact List */}
                <div className="space-y-6">
                    <div className="space-y-6">
                      {getAllOrderedCategories().map((category, categoryIndex) => {
                        const contacts = getAllContactsForCategory(category);
                        const isCatCollapsed = isCategoryCollapsed(category);
                        
                        return (
                          <div 
                            key={category} 
                            ref={(el) => categoryRefs.current[category] = el}
                            className={`border border-border rounded-lg bg-surface shadow-sm hover:shadow-md transition-all duration-300 cursor-move ${
                              highlightedItems.has(`category-${category}`) 
                                ? 'ring-4 ring-primary ring-opacity-75 shadow-xl bg-surface-muted category-highlight-animation' 
                                : ''
                            }`}
                            draggable={true}
                            onDragStart={(e) => handleCategoryDragStart(e, category, categoryIndex)}
                            onDragEnd={handleCategoryDragEnd}
                            onDragOver={handleCategoryDragOver}
                            onDrop={(e) => handleCategoryDrop(e, category, categoryIndex)}
                            title="Drag to reorder this entire category section"
                          >
                            {/* Category Header - Clickable to collapse/expand */}
                            <div 
                              className="flex items-center gap-3 p-4 bg-surface-muted rounded-t-lg border-b border-border cursor-pointer hover:bg-surface transition-colors w-full"
                              onClick={() => toggleCategoryCollapse(category)}
                            >
                              {/* Category drag handle */}
                              <div className="flex flex-col opacity-40 hover:opacity-60 transition-opacity cursor-move" title="Drag to reorder category">
                                <div className="w-2 h-2 bg-text-muted rounded-full mb-0.5"></div>
                                <div className="w-2 h-2 bg-text-muted rounded-full mb-0.5"></div>
                                <div className="w-2 h-2 bg-text-muted rounded-full"></div>
                              </div>
                              
                              {/* Collapse/Expand icon */}
                              <div className="text-text-muted">
                                {isCatCollapsed ? <ChevronRight className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
                              </div>
                              
                              {/* Category icon */}
                              <div className="text-xl">
                                <span>{getCategoryEmoji(category)}</span>
                              </div>
                              
                              {/* Category title */}
                              <h3 className="text-lg font-semibold text-text-primary flex-1 min-w-0 truncate">
                                {category}
                              </h3>
                              
                              {/* Contact count badge */}
                              <Badge variant="secondary" className="shrink-0">
                                {contacts.length} task{contacts.length !== 1 ? 's' : ''}
                              </Badge>
                              
                              {/* Category delete button */}
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={(e) => {
                                  e.stopPropagation(); // Prevent category collapse
                                  confirmDeleteCategory(category);
                                }}
                                className="p-1 h-8 w-8 text-destructive hover:bg-destructive/10 shrink-0"
                                title="Delete this category"
                              >
                                <X className="h-4 w-4" />
                              </Button>
                            </div>

                            {/* Category Contacts - only show if not collapsed */}
                            {!isCatCollapsed && (
                              <div className="p-4 space-y-4">
                                {contacts.map((contact, index) => {
                                  // Handle both master and custom contacts
                                  const isCustomContact = contact.is_custom;
                                  const contactKey = isCustomContact ? contact.id : `${category}-${index}`;
                                  
                                  if (isCustomContact) {
                                    // Render custom contact using simplified approach
                                    return (
                                      <TaskCard
                                        key={contactKey}
                                        task={contact}
                                        taskKey={contactKey}
                                        category={category}
                                        index={index}
                                        isSelected={selectedContacts.has(contact.id)}
                                        isCollapsed={isCustomContactCollapsed(contact.id)}
                                        isInjected={false}
                                        isCustom={true}
                                        customName={contact.customName || contact.name}
                                        instructions={contact.address || ""}
                                        tools={contact.tools || ""}
                                        notes={contact.notes || ""}
                                        onToggle={(checked) => handleCustomContactToggle(contact.id, checked)}
                                        onCollapse={() => toggleCustomContactCollapse(contact.id)}
                                        onDataChange={(field, value) => updateCustomContact(contact.id, field, value)}
                                        onRemove={() => confirmDeleteContact(contact.id, contact.name, true)}
                                        dragHandlers={{
                                          onDragStart: (e) => handleCustomContactDragStart(e, contact.id, category),
                                          onDragEnd: handleCustomContactDragEnd,
                                          onDragOver: handleCustomContactDragOver,
                                          onDrop: (e) => handleCustomContactDrop(e, contact.id, category)
                                        }}
                                      />
                                    );
                                  } else {
                                    // Use existing renderContact for master contacts
                                    return renderContact(contactKey, index, false, null);
                                  }
                                })}
                                
                                {/* Injected contacts for each original contact */}
                                {contacts.filter(contact => !contact.is_custom).map((contact, index) => {
                                  const contactKey = `${category}-${index}`;
                                  const injectedForThisContact = injectedContacts[contactKey] || [];
                                  
                                  return injectedForThisContact.map((injectedContact, injIndex) => (
                                    <div key={`${injectedContact.injectedKey}-${injIndex}`}>
                                      {renderContact(injectedContact.injectedKey, index, true, contact)}
                                    </div>
                                  ));
                                })}

                                {/* Empty category drop zone */}
                                {(!contacts || contacts.length === 0) && (
                                  <div
                                    className="border-2 border-dashed border-border rounded-lg p-8 text-center text-text-muted hover:border-primary hover:bg-surface-muted transition-colors bg-surface"
                                    onDragOver={(e) => {
                                      e.preventDefault();
                                      e.dataTransfer.dropEffect = 'move';
                                      e.target.closest('.border-dashed').classList.add('!border-primary', '!bg-surface-muted');
                                    }}
                                    onDragLeave={(e) => {
                                      e.target.closest('.border-dashed').classList.remove('!border-primary', '!bg-surface-muted');
                                    }}
                                    onDrop={(e) => {
                                      e.preventDefault();
                                      e.target.closest('.border-dashed').classList.remove('!border-primary', '!bg-surface-muted');
                                      
                                      try {
                                        const dragData = JSON.parse(e.dataTransfer.getData('application/json'));
                                        const { contactKey, contactId, category: dragCategory, type } = dragData;
                                        
                                        // Handle both master and custom contacts
                                        if (type === 'custom-contact') {
                                          // Moving custom contact to empty category
                                          const updatedContacts = customContacts.map(contact => 
                                            contact.id === contactId 
                                              ? { ...contact, category: category }
                                              : contact
                                          );
                                          setCustomContacts(updatedContacts);
                                          toast.success(`Contact moved to "${category}"`);
                                        } else if (type === 'contact') {
                                          // Moving master contact to empty category
                                          const [, indexStr] = contactKey.split('-');
                                          const index = parseInt(indexStr);
                                          const originalContact = masterContacts[dragCategory][index];
                                          
                                          if (originalContact) {
                                            // Update master contacts
                                            const updatedMasterContacts = { ...masterContacts };
                                            
                                            // Remove from original category
                                            updatedMasterContacts[dragCategory] = updatedMasterContacts[dragCategory].filter((_, i) => i !== index);
                                            
                                            // Add to empty category
                                            if (!updatedMasterContacts[category]) {
                                              updatedMasterContacts[category] = [];
                                            }
                                            updatedMasterContacts[category].push(originalContact);
                                            
                                            setMasterContacts(updatedMasterContacts);
                                            
                                            // Update contact data keys
                                            const oldContactKey = contactKey;
                                            const newContactKey = `${category}-0`; // First contact in empty category
                                            
                                            if (contactData[oldContactKey]) {
                                              const updatedContactData = { ...contactData };
                                              updatedContactData[newContactKey] = updatedContactData[oldContactKey];
                                              delete updatedContactData[oldContactKey];
                                              setContactData(updatedContactData);
                                            }
                                            
                                            // Update selected contacts
                                            if (selectedContacts.has(oldContactKey)) {
                                              const updatedSelectedContacts = new Set(selectedContacts);
                                              updatedSelectedContacts.delete(oldContactKey);
                                              updatedSelectedContacts.add(newContactKey);
                                              setSelectedContacts(updatedSelectedContacts);
                                            }
                                            
                                            toast.success(`Contact moved to "${category}"`);
                                          }
                                        }
                                      } catch (error) {
                                        console.error('Error handling drop on empty category:', error);
                                        toast.error('Error moving contact');
                                      }
                                    }}
                                  >
                                    <div className="flex flex-col items-center gap-2">
                                      <div className="text-4xl">📋</div>
                                      <p className="font-medium">Drop tasks here</p>
                                      <p className="text-sm">This category is empty - drag tasks here to add them</p>
                                    </div>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })}



                    </div>
                  </div>

                    {/* Action Buttons - Bottom */}
                    <div className="flex gap-2 mt-6">
                      <Button 
                        className="flex-1 flex items-center gap-2"
                        onClick={() => setShowCustomContactDialog(true)}
                      >
                        <Plus className="h-4 w-4" />
                        Add Custom Task
                      </Button>

                      <Button 
                        variant="outline" 
                        className="flex-1 flex items-center gap-2"
                        onClick={() => setShowCustomCategoryDialog(true)}
                      >
                        <Plus className="h-4 w-4" />
                        Add Custom Category
                      </Button>
                    </div>
              </CardContent>
            </Card>
          </div>

          {/* Print Readiness Dashboard */}
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  🏠 Home Maintenance Progress Dashboard
                  <div className="flex items-center gap-2">
                    <Badge className={scoreAnimating ? 'animate-bounce-scale' : ''} variant={calculateMaintenanceReadinessScore() >= 80 ? "default" : calculateMaintenanceReadinessScore() >= 50 ? "secondary" : "destructive"}>
                      {calculateMaintenanceReadinessScore()}% Complete
                    </Badge>
                  </div>
                </CardTitle>
                <CardDescription>
                  Track your progress toward a comprehensive home maintenance schedule
                </CardDescription>
              </CardHeader>
              <CardContent>
                {getSelectedTasksCount() === 0 ? (
                  <div className="text-center py-8 text-text-muted">
                    <AlertCircle className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p>No tasks selected yet</p>
                    <p className="text-sm">Select tasks to see your progress</p>
                  </div>
                ) : (
                  <div className="space-y-6">
                    {/* Print Readiness Score Ring */}
                    <div className="text-center">
                      <div className="relative inline-flex items-center justify-center">
                        <svg className="w-24 h-24 transform -rotate-90">
                          <circle
                            cx="48"
                            cy="48"
                            r="40"
                            stroke="currentColor"
                            strokeWidth="8"
                            fill="none"
                            className="text-muted"
                          />
                          <circle
                            cx="48"
                            cy="48"
                            r="40"
                            stroke="currentColor"
                            strokeWidth="8"
                            fill="none"
                            strokeDasharray={`${2 * Math.PI * 40}`}
                            strokeDashoffset={`${2 * Math.PI * 40 * (1 - calculateMaintenanceReadinessScore() / 100)}`}
                            className={
                              calculateMaintenanceReadinessScore() >= 80 
                                ? "text-green-500" 
                                : calculateMaintenanceReadinessScore() >= 50 
                                ? "text-yellow-500" 
                                : "text-red-500"
                            }
                            style={{ transition: 'stroke-dashoffset 0.5s ease-in-out' }}
                          />
                        </svg>
                        <div className="absolute inset-0 flex items-center justify-center">
                          <div className="text-center">
                            <div className={`text-2xl font-bold ${scoreAnimating ? 'animate-bounce-scale' : ''}`}>{calculateMaintenanceReadinessScore()}%</div>
                            <div className="text-xs text-text-muted">Complete</div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Quick Stats Cards - label on top, counts snapped to bottom so they align across cards */}
                    <div className="grid grid-cols-3 gap-3 min-w-0 items-stretch">
                      <div className="bg-surface-muted rounded-lg p-3 min-w-0 flex flex-col">
                        <p className="text-sm font-medium text-text-muted leading-tight break-words">Total Tasks</p>
                        <div className="flex-1 min-h-0" aria-hidden="true" />
                        <div className="flex items-end gap-2 flex-shrink-0">
                          <div className="flex-shrink-0 w-7 h-7 flex items-center justify-center">
                            <Home className="h-5 w-5 text-primary" />
                          </div>
                          <p className="text-2xl font-bold text-text-primary leading-tight tabular-nums">{getTaskCompletionStats().total}</p>
                        </div>
                      </div>
                      <div className="bg-surface-muted rounded-lg p-3 min-w-0 flex flex-col">
                        <p className="text-sm font-medium text-text-muted leading-tight break-words">Instructions</p>
                        <div className="flex-1 min-h-0" aria-hidden="true" />
                        <div className="flex items-end gap-2 flex-shrink-0">
                          <div className="flex-shrink-0 w-7 h-7 flex items-center justify-center">
                            <FileText className="h-5 w-5 text-primary" />
                          </div>
                          <p className="text-2xl font-bold text-primary leading-tight tabular-nums">{getTaskCompletionStats().withInstructions}</p>
                        </div>
                      </div>
                      <div className="bg-surface-muted rounded-lg p-3 min-w-0 flex flex-col">
                        <p className="text-sm font-medium text-text-muted leading-tight break-words">Tools & Supplies</p>
                        <div className="flex-1 min-h-0" aria-hidden="true" />
                        <div className="flex items-end gap-2 flex-shrink-0">
                          <div className="flex-shrink-0 w-7 h-7 flex items-center justify-center">
                            <Wrench className="h-5 w-5 text-primary" />
                          </div>
                          <p className="text-2xl font-bold text-text-primary leading-tight tabular-nums">{getTaskCompletionStats().withTools}</p>
                        </div>
                      </div>
                    </div>

                    {/* Data Completion Progress */}
                    <div className="space-y-4">
                      <h4 className="text-sm font-medium text-text-primary">Task Information Completion</h4>
                      
                      {/* Instructions Progress */}
                      <div className="space-y-2">
                        <div className="flex justify-between text-sm gap-2">
                          <span className="flex items-center gap-1 min-w-0">
                            <FileText className="h-4 w-4 flex-shrink-0" />
                            Instructions Added
                          </span>
                          <span className="text-text-muted flex-shrink-0">
                            {getTaskCompletionStats().withInstructions}/{getTaskCompletionStats().total}
                          </span>
                        </div>
                        <div className="w-full bg-muted rounded-full h-2">
                          <div 
                            className="bg-primary h-2 rounded-full transition-all duration-500 ease-out"
                            style={{ 
                              width: `${getTaskCompletionStats().total > 0 
                                ? (getTaskCompletionStats().withInstructions / getTaskCompletionStats().total) * 100 
                                : 0}%` 
                            }}
                          ></div>
                        </div>
                      </div>

                      {/* Tools Progress */}
                      <div className="space-y-2">
                        <div className="flex justify-between text-sm gap-2">
                          <span className="flex items-center gap-1 min-w-0">
                            🔧 Tools & Supplies Listed
                          </span>
                          <span className="text-text-muted flex-shrink-0">
                            {getTaskCompletionStats().withTools}/{getTaskCompletionStats().total}
                          </span>
                        </div>
                        <div className="w-full bg-muted rounded-full h-2">
                          <div 
                            className="bg-primary h-2 rounded-full transition-all duration-500 ease-out"
                            style={{ 
                              width: `${getTaskCompletionStats().total > 0 
                                ? (getTaskCompletionStats().withTools / getTaskCompletionStats().total) * 100 
                                : 0}%` 
                            }}
                          ></div>
                        </div>
                      </div>

                      {/* Fully Complete Progress */}
                      <div className="space-y-2">
                        <div className="flex justify-between text-sm gap-2">
                          <span className="flex items-center gap-1 min-w-0">
                            <CheckCircle className="h-4 w-4 flex-shrink-0" />
                            Fully Prepared Tasks
                          </span>
                          <span className="text-text-muted flex-shrink-0">
                            {getTaskCompletionStats().fullyComplete}/{getTaskCompletionStats().total}
                          </span>
                        </div>
                        <div className="w-full bg-muted rounded-full h-2">
                          <div 
                            className="bg-primary h-2 rounded-full transition-all duration-500 ease-out"
                            style={{ 
                              width: `${getTaskCompletionStats().total > 0 
                                ? (getTaskCompletionStats().fullyComplete / getTaskCompletionStats().total) * 100 
                                : 0}%` 
                            }}
                          ></div>
                        </div>
                      </div>
                    </div>

                    {/* Category Coverage */}
                    <div className="space-y-3">
                      <h4 className="text-sm font-medium text-text-primary">Monthly Coverage</h4>
                      <div className="grid gap-2">
                        {Object.entries(getTaskCompletionStats().byCategory).map(([category, data]) => (
                          <div key={category} className="flex items-center justify-between gap-2 p-2 bg-surface-muted rounded min-w-0">
                            <div className="flex items-center gap-2 min-w-0">
                              <span className="text-lg flex-shrink-0">{getCategoryEmoji(category)}</span>
                              <span className="text-sm font-medium truncate text-text-primary" title={category}>
                                {category}
                              </span>
                            </div>
                            <div className="flex items-center gap-2 flex-shrink-0">
                              <span className="text-xs text-text-muted">{data.complete}/{data.total}</span>
                              <div className="w-12 bg-muted rounded-full h-1.5">
                                <div 
                                  className={`h-1.5 rounded-full transition-all duration-300 ${
                                    data.complete === data.total ? 'bg-green-500' : 
                                    data.complete > 0 ? 'bg-yellow-500' : 'bg-red-500'
                                  }`}
                                  style={{ width: `${(data.complete / data.total) * 100}%` }}
                                ></div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Achievement Badges */}
                    {(getTaskCompletionStats().total >= 10 || 
                      Object.keys(getTaskCompletionStats().byCategory).length >= 5 || 
                      calculateMaintenanceReadinessScore() >= 80 ||
                      getTaskCompletionStats().withTools >= 5 ||
                      getTaskCompletionStats().fullyComplete >= 5) && (
                      <div className="space-y-3">
                        <h4 className="text-sm font-medium text-text-primary">Achievements</h4>
                        <div className="flex flex-wrap gap-2">
                          {getTaskCompletionStats().total >= 10 && (
                            <Badge variant="outline" className="bg-primary/10 text-primary border-primary/30">
                              🏠 Task Master (10+ tasks)
                            </Badge>
                          )}
                          {Object.keys(getTaskCompletionStats().byCategory).length >= 5 && (
                            <Badge variant="outline" className="bg-primary/10 text-primary border-primary/30">
                              📅 Maintenance Pro (5+ months)
                            </Badge>
                          )}
                          {getTaskCompletionStats().withTools >= 5 && (
                            <Badge variant="outline" className="bg-primary/10 text-primary border-primary/30">
                              🔧 Tool Tracker (5+ tool lists)
                            </Badge>
                          )}
                          {getTaskCompletionStats().fullyComplete >= 5 && (
                            <Badge variant="outline" className="bg-primary/10 text-primary border-primary/30">
                              📋 Detail Master (5+ fully prepared)
                            </Badge>
                          )}
                          {calculateMaintenanceReadinessScore() >= 80 && (
                            <Badge variant="outline" className="bg-primary/10 text-primary border-primary/30">
                              ✅ Maintenance Ready (80%+ complete)
                            </Badge>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Print Templates */}
            {getSelectedTasksCount() > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Printer className="h-5 w-5" />
                    Print Templates
                  </CardTitle>
                  <CardDescription>
                    Choose your preferred format for printing maintenance schedules
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-3">
                  <Button
                    onClick={() => printMaintenanceSchedule('master')}
                    className="w-full justify-between"
                    variant="outline"
                  >
                    <div className="flex items-center">
                      <FileText className="h-4 w-4 mr-2" />
                      Master Schedule
                    </div>
                    <Badge variant="secondary">
                      Planning Reference
                    </Badge>
                  </Button>
                  
                  <Button
                    onClick={() => printMaintenanceSchedule('checklist')}
                    className="w-full justify-between"
                    variant="outline"
                  >
                    <div className="flex items-center">
                      <CheckCircle className="h-4 w-4 mr-2" />
                      Task Checklist
                    </div>
                    <Badge variant="secondary">
                      While Working
                    </Badge>
                  </Button>
                  
                  <Button
                    onClick={() => printMaintenanceSchedule('log')}
                    className="w-full justify-between"
                    variant="outline"
                  >
                    <div className="flex items-center">
                      <Save className="h-4 w-4 mr-2" />
                      Maintenance Log
                    </div>
                    <Badge variant="secondary">
                      Record Keeping
                    </Badge>
                  </Button>
                </CardContent>
              </Card>
            )}
          </div>
        </div>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteConfirmDialog} onOpenChange={setShowDeleteConfirmDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm Deletion</DialogTitle>
            <DialogDescription>
              {deleteConfirmData.type === 'category' ? (
                <>
                  Are you sure you want to delete the category "{deleteConfirmData.name}"?
                  <br />
                  <strong>This will also delete all contacts in this category and cannot be undone.</strong>
                </>
              ) : (
                <>
                  Are you sure you want to delete the contact "{deleteConfirmData.name}"?
                  <br />
                  <strong>This action cannot be undone.</strong>
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end gap-2">
            <Button
              variant="outline"
              onClick={() => setShowDeleteConfirmDialog(false)}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={executeDelete}
            >
              Delete
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      </>
    </ToolLayout>
  );
}

export default MaintenanceApp;